/**
 * Servicio de email utilizando SendGrid para VV Health
 * 
 * Este servicio maneja el envío de notificaciones por email a los usuarios
 * para diferentes eventos relacionados con cotizaciones.
 */

import sgMail from '@sendgrid/mail';

// Configuración de la API key de SendGrid
// La API key debe estar configurada como variable de entorno
// En Vite, las variables de entorno deben tener el prefijo VITE_
const SENDGRID_API_KEY = import.meta.env.VITE_SENDGRID_API_KEY;
const FROM_EMAIL = import.meta.env.VITE_FROM_EMAIL || 'notificaciones@vvhealth.com';
const FROM_NAME = import.meta.env.VITE_FROM_NAME || 'VV Health';

// Inicializar SendGrid con la API key
if (SENDGRID_API_KEY) {
  sgMail.setApiKey(SENDGRID_API_KEY);
  console.log('SendGrid inicializado correctamente con la API key configurada');
} else {
  console.warn('ADVERTENCIA: SENDGRID_API_KEY no está configurada. Los emails no serán enviados.');
}

/**
 * Obtiene la URL base del servidor para las llamadas a la API
 * @returns {string} URL base del servidor
 */
const getServerUrl = () => {
  // En desarrollo, usamos la URL completa del servidor local
  // En producción, usamos la URL completa de la aplicación
  const isProduction = import.meta.env.PROD;
  
  if (isProduction) {
    const appUrl = import.meta.env.VITE_APP_URL || 'https://vvhealth.vercel.app';
    console.log('Usando URL del servidor en producción:', appUrl);
    return appUrl;
  }
  
  // En desarrollo, usamos la URL del servidor local
  console.log('Usando URL del servidor local para desarrollo: http://localhost:3030');
  return 'http://localhost:3030';
};

/**
 * Constantes para los IDs de las plantillas de SendGrid
 */
export const TEMPLATE_IDS = {
  WELCOME_QUOTATION: 'd-ba7ccce6548041b09426d3d605fe88f7', // Email Bienvenida y Solicitud de Estimado
  QUOTATION_READY: 'd-cc9fd0381d284313be5b5d114fed15fe'    // Email Estimado Listo
};

/**
 * Envía un email utilizando SendGrid
 * @param {Object} options - Opciones del email
 * @param {string} options.to - Dirección de email del destinatario
 * @param {string} options.subject - Asunto del email
 * @param {string} options.text - Contenido del email en texto plano
 * @param {string} options.html - Contenido del email en HTML
 * @param {Array} options.attachments - Archivos adjuntos (opcional)
 * @returns {Promise} - Promesa que se resuelve cuando el email es enviado
 */
export const sendEmail = async (options) => {
  if (!SENDGRID_API_KEY) {
    console.warn('No se puede enviar email: SENDGRID_API_KEY no está configurada');
    return null;
  }

  try {
    // Usamos la API route de Vercel para enviar emails
    // En desarrollo usará el servidor local, en producción usará la función serverless
    const serverUrl = getServerUrl();
    const emailServerUrl = `${serverUrl}/api/send-email`;
    
    console.log('Enviando email a través de:', emailServerUrl, {
      to: options.to,
      subject: options.subject,
      hasText: !!options.text,
      hasHtml: !!options.html,
      attachmentsCount: (options.attachments || []).length
    });
    
    const response = await fetch(emailServerUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        to: options.to,
        subject: options.subject,
        text: options.text,
        html: options.html,
        attachments: options.attachments
      }),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Error del servidor de emails (${response.status}):`, errorText);
      throw new Error(`Error al enviar email: ${response.status} ${response.statusText}`);
    }
    
    const result = await response.json();
    
    if (!result.success) {
      console.error('Error del servidor de emails:', result);
      throw new Error(result.message || 'Error al enviar email');
    }
    
    console.log(`Email enviado exitosamente a ${options.to}`);
    return result;
  } catch (error) {
    console.error('Error al enviar email:', error);
    if (error.response) {
      console.error('Detalles del error:', error.response.body);
    }
    throw error;
  }
};

/**
 * Envía un email de confirmación de cotización
 * @param {Object} quotation - Objeto de cotización
 * @param {string} userEmail - Email del usuario
 * @param {boolean} simulateOnly - Si es true, solo devuelve el contenido del email sin enviarlo
 * @returns {Promise} - Promesa que se resuelve cuando el email es enviado o con el contenido si simulateOnly es true
 */
export const sendQuotationConfirmation = async (quotation, userEmail, simulateOnly = false) => {
  const magicLink = quotation.get('magicLink');
  const trackingUrl = `${import.meta.env.VITE_APP_URL || 'https://vvhealth.com'}/quotation/track/${magicLink}`;
  const items = quotation.get('items') || [];
  
  // Generar la lista de productos en HTML
  const productListHtml = items.map(item => {
    return `
      <tr>
        <td style="padding: 8px; border-bottom: 1px solid #ddd;">${item.name}</td>
        <td style="padding: 8px; border-bottom: 1px solid #ddd;">${item.selectedFormat?.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'N/A'}</td>
        <td style="padding: 8px; border-bottom: 1px solid #ddd;">${item.bulkQuantity} unidades</td>
      </tr>
    `;
  }).join('');

  // Generar la lista de productos en texto plano
  const productListText = items.map(item => {
    return `- ${item.name} (${item.selectedFormat?.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'N/A'}): ${item.bulkQuantity} unidades`;
  }).join('\n');

  const subject = 'Confirmación de Cotización - VV Health';
  
  const text = `
    Hola,

    Gracias por solicitar una cotización en VV Health.

    Detalles de tu cotización:
    ID: ${quotation.id}
    Fecha: ${new Date(quotation.createdAt).toLocaleDateString()}
    Estado: Pendiente

    Productos:
    ${productListText}

    Puedes seguir el estado de tu cotización en el siguiente enlace:
    ${trackingUrl}

    Si tienes alguna pregunta, no dudes en contactarnos.

    Saludos,
    Equipo VV Health
  `;

  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <title>Confirmación de Cotización</title>
      <style>
        body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
        .container { max-width: 600px; margin: 0 auto; padding: 20px; }
        .header { background-color: #f8f9fa; padding: 20px; text-align: center; }
        .content { padding: 20px; }
        .footer { background-color: #f8f9fa; padding: 20px; text-align: center; font-size: 12px; color: #666; }
        .button { display: inline-block; background-color: #007bff; color: white; text-decoration: none; padding: 10px 20px; border-radius: 4px; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
        th { background-color: #f8f9fa; text-align: left; padding: 8px; border-bottom: 2px solid #ddd; }
      </style>
    </head>
    <body>
      <div class="container">
        <div class="header">
          <h2>VV Health</h2>
          <p>Confirmación de Cotización</p>
        </div>
        <div class="content">
          <p>Hola,</p>
          <p>Gracias por solicitar una cotización en VV Health.</p>
          
          <h3>Detalles de tu cotización:</h3>
          <p>
            <strong>ID:</strong> ${quotation.id}<br>
            <strong>Fecha:</strong> ${new Date(quotation.createdAt).toLocaleDateString()}<br>
            <strong>Estado:</strong> <span style="color: #ffc107;">Pendiente</span>
          </p>
          
          <h3>Productos:</h3>
          <table>
            <thead>
              <tr>
                <th>Producto</th>
                <th>Formato</th>
                <th>Cantidad</th>
              </tr>
            </thead>
            <tbody>
              ${productListHtml}
            </tbody>
          </table>
          
          <p>Puedes seguir el estado de tu cotización en el siguiente enlace:</p>
          <p style="text-align: center;">
            <a href="${trackingUrl}" class="button">Ver estado de cotización</a>
          </p>
          
          <p>Si tienes alguna pregunta, no dudes en contactarnos.</p>
          
          <p>Saludos,<br>Equipo VV Health</p>
        </div>
        <div class="footer">
          <p>© ${new Date().getFullYear()} VV Health. Todos los derechos reservados.</p>
          <p>Este email fue enviado a ${userEmail}</p>
        </div>
      </div>
    </body>
    </html>
  `;

  return sendEmail({
    to: userEmail,
    subject,
    text,
    html
  });
};

/**
 * Envía un email de actualización de estado de cotización
 * @param {Object} quotation - Objeto de cotización
 * @param {string} userEmail - Email del usuario
 * @param {string} oldStatus - Estado anterior de la cotización
 * @param {string} newStatus - Nuevo estado de la cotización
 * @returns {Promise} - Promesa que se resuelve cuando el email es enviado
 */
export const sendStatusUpdateNotification = async (quotation, userEmail, oldStatus, newStatus) => {
  const magicLink = quotation.get('magicLink');
  const trackingUrl = `${import.meta.env.VITE_APP_URL || 'https://vvhealth.com'}/quotation/track/${magicLink}`;
  
  const statusLabels = {
    pending: 'Pendiente',
    estimado_listo: 'Estimado Listo',
    completada: 'Completada',
    rechazada: 'Rechazada'
  };

  const subject = `Actualización de Cotización - VV Health`;
  
  const text = `
    Hola,

    El estado de tu cotización ha sido actualizado.

    Detalles de tu cotización:
    ID: ${quotation.id}
    Fecha: ${new Date(quotation.createdAt).toLocaleDateString()}
    Estado anterior: ${statusLabels[oldStatus] || oldStatus}
    Nuevo estado: ${statusLabels[newStatus] || newStatus}

    Puedes ver los detalles completos de tu cotización en el siguiente enlace:
    ${trackingUrl}

    Si tienes alguna pregunta, no dudes en contactarnos.

    Saludos,
    Equipo VV Health
  `;

  const getStatusColor = (status) => {
    const colors = {
      pending: '#ffc107',
      estimado_listo: '#17a2b8',
      completada: '#28a745',
      rechazada: '#dc3545'
    };
    return colors[status] || '#6c757d';
  };

  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <title>Actualización de Cotización</title>
      <style>
        body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
        .container { max-width: 600px; margin: 0 auto; padding: 20px; }
        .header { background-color: #f8f9fa; padding: 20px; text-align: center; }
        .content { padding: 20px; }
        .footer { background-color: #f8f9fa; padding: 20px; text-align: center; font-size: 12px; color: #666; }
        .button { display: inline-block; background-color: #007bff; color: white; text-decoration: none; padding: 10px 20px; border-radius: 4px; }
        .status-badge { display: inline-block; padding: 5px 10px; border-radius: 4px; font-weight: bold; }
        .status-change { display: flex; align-items: center; justify-content: center; margin: 20px 0; }
        .arrow { margin: 0 15px; font-size: 20px; }
      </style>
    </head>
    <body>
      <div class="container">
        <div class="header">
          <h2>VV Health</h2>
          <p>Actualización de Cotización</p>
        </div>
        <div class="content">
          <p>Hola,</p>
          <p>El estado de tu cotización ha sido actualizado.</p>
          
          <h3>Detalles de tu cotización:</h3>
          <p>
            <strong>ID:</strong> ${quotation.id}<br>
            <strong>Fecha:</strong> ${new Date(quotation.createdAt).toLocaleDateString()}
          </p>
          
          <div class="status-change">
            <span class="status-badge" style="background-color: ${getStatusColor(oldStatus)}; color: white;">
              ${statusLabels[oldStatus] || oldStatus}
            </span>
            <span class="arrow">→</span>
            <span class="status-badge" style="background-color: ${getStatusColor(newStatus)}; color: white;">
              ${statusLabels[newStatus] || newStatus}
            </span>
          </div>
          
          <p>Puedes ver los detalles completos de tu cotización en el siguiente enlace:</p>
          <p style="text-align: center;">
            <a href="${trackingUrl}" class="button">Ver detalles de cotización</a>
          </p>
          
          <p>Si tienes alguna pregunta, no dudes en contactarnos.</p>
          
          <p>Saludos,<br>Equipo VV Health</p>
        </div>
        <div class="footer">
          <p>© ${new Date().getFullYear()} VV Health. Todos los derechos reservados.</p>
          <p>Este email fue enviado a ${userEmail}</p>
        </div>
      </div>
    </body>
    </html>
  `;

  return sendEmail({
    to: userEmail,
    subject,
    text,
    html
  });
};

/**
 * Envía un email de bienvenida cuando un usuario crea una cuenta
 * @param {Object} user - Objeto de usuario
 * @returns {Promise} - Promesa que se resuelve cuando el email es enviado
 */
export const sendWelcomeEmail = async (user) => {
  const userEmail = user.get('email');
  const userName = user.get('name') || 'Estimado cliente';
  const dashboardUrl = `${import.meta.env.VITE_APP_URL || 'https://vvhealth.com'}/dashboard/quotations`;

  const subject = 'Bienvenido a VV Health';
  
  const text = `
    Hola ${userName},

    ¡Bienvenido a VV Health!

    Tu cuenta ha sido creada exitosamente. Ahora puedes acceder a tu panel de control para ver tus cotizaciones y realizar seguimiento de tus pedidos.

    Accede a tu panel de control aquí:
    ${dashboardUrl}

    Si tienes alguna pregunta, no dudes en contactarnos.

    Saludos,
    Equipo VV Health
  `;

  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <title>Bienvenido a VV Health</title>
      <style>
        body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
        .container { max-width: 600px; margin: 0 auto; padding: 20px; }
        .header { background-color: #f8f9fa; padding: 20px; text-align: center; }
        .content { padding: 20px; }
        .footer { background-color: #f8f9fa; padding: 20px; text-align: center; font-size: 12px; color: #666; }
        .button { display: inline-block; background-color: #007bff; color: white; text-decoration: none; padding: 10px 20px; border-radius: 4px; }
      </style>
    </head>
    <body>
      <div class="container">
        <div class="header">
          <h2>VV Health</h2>
          <p>¡Bienvenido!</p>
        </div>
        <div class="content">
          <p>Hola ${userName},</p>
          <p>¡Bienvenido a VV Health!</p>
          
          <p>Tu cuenta ha sido creada exitosamente. Ahora puedes acceder a tu panel de control para ver tus cotizaciones y realizar seguimiento de tus pedidos.</p>
          
          <p style="text-align: center; margin: 30px 0;">
            <a href="${dashboardUrl}" class="button">Acceder a mi panel de control</a>
          </p>
          
          <p>Si tienes alguna pregunta, no dudes en contactarnos.</p>
          
          <p>Saludos,<br>Equipo VV Health</p>
        </div>
        <div class="footer">
          <p>© ${new Date().getFullYear()} VV Health. Todos los derechos reservados.</p>
          <p>Este email fue enviado a ${userEmail}</p>
        </div>
      </div>
    </body>
    </html>
  `;

  return sendEmail({
    to: userEmail,
    subject,
    text,
    html
  });
};

/**
 * Envía un email utilizando una plantilla de SendGrid
 * @param {Object} options - Opciones del email
 * @param {string} options.to - Dirección de email del destinatario
 * @param {string} options.templateId - ID de la plantilla de SendGrid
 * @param {Object} options.dynamicTemplateData - Datos dinámicos para la plantilla
 * @returns {Promise} - Promesa que se resuelve cuando el email es enviado
 */
export const sendTemplateEmail = async (options) => {
  if (!SENDGRID_API_KEY) {
    console.warn('No se puede enviar email: SENDGRID_API_KEY no está configurada');
    return null;
  }

  try {
    // Usamos la API route de Vercel para enviar emails con plantillas
    // En desarrollo usará el servidor local, en producción usará la función serverless
    const serverUrl = getServerUrl();
    const emailServerUrl = `${serverUrl}/api/send-template-email`;
    
    console.log('Enviando email con plantilla a través de:', emailServerUrl, {
      to: options.to,
      templateId: options.templateId,
      dynamicTemplateDataKeys: Object.keys(options.dynamicTemplateData || {})
    });
    
    const response = await fetch(emailServerUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        to: options.to,
        templateId: options.templateId,
        dynamicTemplateData: options.dynamicTemplateData || {}
      }),
    });

    if (!response.ok) {
      let errorMessage = `Error HTTP ${response.status} ${response.statusText}`;
      try {
        const errorData = await response.json();
        console.error('Error al enviar email con plantilla:', errorData);
        errorMessage = errorData.message || errorMessage;
      } catch (e) {
        const errorText = await response.text();
        console.error(`Error al enviar email con plantilla (${response.status}):`, errorText);
      }
      throw new Error(`Error al enviar email: ${errorMessage}`);
    }

    const data = await response.json();
    console.log(`Email con plantilla ${options.templateId} enviado exitosamente a ${options.to}`);
    return data.success;
  } catch (error) {
    console.error('Error al enviar email con plantilla:', error);
    return false;
  }
};

export default {
  sendEmail,
  sendTemplateEmail,
  sendQuotationConfirmation,
  sendStatusUpdateNotification,
  sendWelcomeEmail,
  TEMPLATE_IDS
};
